import { getAuth } from '../auth/core/AuthHelpers';

type Auth = {
  token?: string | null;
  refresh?: () => Promise<string | null> | null;
};

type BaseURLTypes = 'ethoca' | 'ics' | 'oi' | 'ics-message' | 'rdr' | 'cbp-service' | 'statistics' | 'tld-log' | 'si' | 'shufti-pro' | 'site-intelligence' | 'tld' | 'cdrn';

let refreshInFlight: Promise<string | null> | null = null;

async function refreshTokenWithLock(refresh?: Auth['refresh']): Promise<string | null> {
  if (!refresh) {
    return null;
  }

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        return await Promise.resolve(refresh());
      } finally {
        refreshInFlight = null;
      }
    })();
  }

  return refreshInFlight;
}

export class HTTPError extends Error {
  public constructor(public response: Response) {
    super();
  }
}
export class Unauthenticated extends Error { }

export async function api(
  path: string,
  options: RequestInit,
  { token, refresh }: Auth,
  serviceUrlType: BaseURLTypes = 'ethoca',
  type: 'INTERNAL' | 'MERCHANT' = 'INTERNAL'
): Promise<Response | null> {
  try {
    const resolvedToken = token ?? getAuth()?.accessToken;
    let serviceUrl: string;
    switch (serviceUrlType) {
      case 'ics':
        serviceUrl = 'ics/';
        break;
      case 'cdrn':
        serviceUrl = 'cdrn/';
        break;
      case 'oi':
        serviceUrl = 'oi/';
        break;
      case 'ics-message':
        serviceUrl = 'ics-message/';
        break
      case 'rdr':
        serviceUrl = 'rdr/';
        break;
      case 'cbp-service':
        break;
      case 'statistics':
        serviceUrl = 'statistics/';
        break;
      case 'tld-log':
        serviceUrl = 'tld-log/';
        break;
      case 'si':
        serviceUrl = 'si/';
        break;
      case 'shufti-pro':
        serviceUrl = 'shuftipro/';
        break;
      case 'site-intelligence':
        break;
      case 'tld':
        serviceUrl = 'tld/';
        break;
      case 'ethoca':
      default:
        serviceUrl = 'ethoca/';
        break;
    }

    if (!resolvedToken && type === 'INTERNAL') {
      const newToken = await refreshTokenWithLock(refresh);
      if (newToken) {
        return await api(path, options, {
          token: newToken,
          refresh: () => null,
        }, serviceUrlType);
      } else {
        throw new Unauthenticated('Unable to authenticate');
      }
    }

    let response: Response;
    if (serviceUrlType === 'site-intelligence') {
      response = await fetch(new URL(path, process.env.REACT_APP_SITE_INTELLIGENCE_BACKEND_SERVICE_BASE_URL).href, {
        ...options,
        headers: {
          authorization: `Bearer ${resolvedToken}`,
          'X-Service-Client': 'cbp', // NOTE: only for SI Services
          ...options?.headers,
        },
      });

      // 1. Check the header of the original response
      const contentType = response.headers.get('content-type');

      // 2. Only clone if it's JSON and the body hasn't been read
      if (contentType && contentType.includes('application/json') && !response.bodyUsed) {
        try {
          // 3. Safe to clone
          const clonedResponse = response.clone();
          const data = await clonedResponse.json();

          // 4. Execute 401 interception logic
          if (data.code === 401) {
            const newToken = await refreshTokenWithLock(refresh);
            if (newToken) {
              return await api(path, options, {
                token: newToken,
                refresh: null,
              }, serviceUrlType);
            }
          }
        } catch (error) {
          // Intercept unexpected parsing failure, including clone failure, or json unexpected parsing failure
        }
      }
    } else {
      response = await fetch(new URL(path, process.env.REACT_APP_CBP_SERVICE_BASE_URL + serviceUrl).href, {
        ...options,
        headers: {
          authorization: `Bearer ${resolvedToken}`,
          ...options?.headers,
        },
      });

      // TODO: Update handle for token expired and if refresh failed logout
      if (response.status === 401) {
        const newToken = await refreshTokenWithLock(refresh);
        if (newToken) {
          return await api(path, options, {
            token: newToken,
            refresh: () => null,
          }, serviceUrlType);
        }
      }
    }

    // TODO: Add handle for unauth

    if (!response.ok) {
      throw new HTTPError(response);
    }

    return response;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return null;
    }
    throw err;
  }
}